import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const isEmptyParamValue = (v) => (
  v == null || v === '' || (Array.isArray(v) && !v.length)
);

const useUrlSearch = ({ commaKeys = [] } = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = useMemo(
    () => Number(searchParams.get('page') ?? 0) || 0,
    [searchParams]
  );

  const setPage = useCallback((newPage) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.set('page', String(newPage));
      return sp;
    });
  }, [setSearchParams]);

  const setParams = useCallback((updates) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('page', '0');
      Object.entries(updates).forEach(([k, v]) => {
        if (isEmptyParamValue(v)) {
          params.delete(k);
          return;
        }

        if (!Array.isArray(v)) {
          params.set(k, String(v));
          return;
        }

        const filtered = v.filter((val) => !isEmptyParamValue(val));
        if (commaKeys.includes(k)) {
          params.set(k, filtered.join(','));
        } else {
          params.delete(k);
          filtered.forEach((val) => params.append(k, String(val)));
        }
      });

      return params;
    }, { replace: true });
  }, [setSearchParams, commaKeys]);

  return {
    searchParams, page, setPage, setParams
  };
};

export default useUrlSearch;
